import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, FileText, FileArchive, Loader2, CloudUpload } from 'lucide-react';
import { store } from '../lib/store';
import { exportPdf, exportZip } from '../lib/export';
import { useToast } from '../components/Toast';
import { Eyebrow } from '../components/ui';

const BACKUP_KEY = 'archivio.autobackup';
type Format = 'pdf' | 'zip';

export function ExportScreen() {
  const navigate = useNavigate();
  const { show } = useToast();
  const [format, setFormat] = useState<Format>('pdf');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [autoBackup, setAutoBackup] = useState(() => {
    try { return localStorage.getItem(BACKUP_KEY) === '1'; } catch { return false; }
  });

  const toggleBackup = () => {
    const next = !autoBackup;
    setAutoBackup(next);
    try { localStorage.setItem(BACKUP_KEY, next ? '1' : '0'); } catch { /* ignora */ }
    show(next ? 'Backup automatico attivo' : 'Backup automatico disattivato');
  };

  const onExport = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const [docs, warr] = await Promise.all([store.documents(), store.warranties()]);
      if (format === 'pdf') {
        await exportPdf(docs, warr.items);
      } else {
        await exportZip(docs, warr.items, password.trim() || undefined);
      }
      show('Export generato');
    } catch (e) {
      show(e instanceof Error ? e.message : 'Export non riuscito');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="screen">
      <div className="topbar">
        <button className="iconbtn" onClick={() => navigate(-1)} aria-label="Indietro">
          <ChevronLeft size={20} />
        </button>
        <div className="topbar__title">Export & backup</div>
        <span style={{ width: 40 }} />
      </div>

      <Eyebrow>Formato</Eyebrow>
      <div className="exportopts" style={{ marginTop: 11, marginBottom: 20 }}>
        <button
          type="button"
          className={`exportopt${format === 'pdf' ? ' is-active' : ''}`}
          onClick={() => setFormat('pdf')}
        >
          <FileText size={24} />
          <div className="exportopt__name">PDF unico</div>
          <div className="exportopt__desc">Tutti i dati testuali</div>
        </button>
        <button
          type="button"
          className={`exportopt${format === 'zip' ? ' is-active' : ''}`}
          onClick={() => setFormat('zip')}
        >
          <FileArchive size={24} />
          <div className="exportopt__name">ZIP cifrato</div>
          <div className="exportopt__desc">JSON + testo, password</div>
        </button>
      </div>

      {format === 'zip' && (
        <>
          <Eyebrow>Password (facoltativa)</Eyebrow>
          <input
            className="field__input"
            style={{ marginTop: 10, marginBottom: 20 }}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Cifra il backup con una password"
          />
        </>
      )}

      <div className="card" style={{ padding: 14, marginBottom: 20 }}>
        <div className="optionrow">
          <CloudUpload size={20} style={{ color: 'var(--accent)' }} />
          <div className="optionrow__body">
            <div className="optionrow__title">Backup automatico giornaliero</div>
            <div className="optionrow__sub">Copia cifrata su Cloudflare ogni giorno</div>
          </div>
          <button
            type="button"
            className={`switch${autoBackup ? ' is-on' : ''}`}
            onClick={toggleBackup}
            aria-pressed={autoBackup}
            aria-label="Backup automatico"
          >
            <span className="switch__knob" />
          </button>
        </div>
      </div>

      <button className="btn btn--accent btn--block" onClick={onExport} disabled={busy}>
        {busy ? <><Loader2 size={18} className="spin" /> Generazione…</> : 'Esporta ora'}
      </button>
    </div>
  );
}
